import { Router } from '@angular/router';
import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import {
    map,
    tap,
    switchMap,
    catchError,
    withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as saveAs from 'file-saver';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
    resetActivities,
    refreshActivities,
} from '@qwyk/shared-stores/activities';

import * as fromQuotations from './quotations.reducer';
import * as QuotationsActions from './quotations.actions';
import * as QuotationsSelectors from './quotations.selectors';
import { QuotationsServiceBase } from '../quotations-service-base';

@Injectable()
export class QuotationsEffects {

    loadQuotations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuotationsActions.loadQuotations),
            withLatestFrom(
                this.store.select(QuotationsSelectors.getQuotationsQuery)
            ),
            switchMap(([, queryFromStore]) => {
                return this.service.getQuotations(queryFromStore).pipe(
                    map(quotations =>
                        QuotationsActions.loadQuotationsSuccess({
                            quotations: quotations.data,
                            pagination: quotations.meta,
                        })
                    ),
                    catchError(error =>
                        of(QuotationsActions.loadQuotationsFailure({ error }))
                    )
                );
            })
        )
    );

    loadQuotation = createEffect(() =>
        this.actions$.pipe(
            ofType(QuotationsActions.loadQuotation),
            switchMap(action =>
                this.service.getQuotation(action.id).pipe(
                    map(quotation =>
                        QuotationsActions.loadQuotationSuccess({
                            quotation,
                        })
                    ),
                    catchError(error =>
                        of(QuotationsActions.loadQuotationFailure({ error }))
                    )
                )
            )
        )
    );

    loadQuotationsError$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuotationsActions.loadQuotationsFailure),
                tap(action => {
                    this.router.navigate([`/error/${action.error.status}`], {
                        queryParams: {
                            source: this.router.url,
                            errorName: action.error.statusText,
                        },
                    });
                })
            ),
        {
            dispatch: false,
        }
    );

    quotationChanged$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuotationsActions.quotationChanged),
            map(() => refreshActivities())
        )
    );

    unselectQuotation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuotationsActions.unselectQuotation),
            map(() => resetActivities())
        )
    );

    downloadQuotation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuotationsActions.downloadQuotation),
            switchMap(action =>
                this.service.downloadQuotation(action.quotation.id, null).pipe(
                    map((blob: Blob) =>
                        QuotationsActions.downloadQuotationSuccess({
                            quotation: action.quotation,
                            blob,
                        })
                    ),
                    catchError(error =>
                        of(
                            QuotationsActions.downloadQuotationFailure({
                                error,
                            })
                        )
                    )
                )
            )
        )
    );

    downloadQuotationSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QuotationsActions.downloadQuotationSuccess),
                tap(action => {
                    saveAs(action.blob, `${action.quotation.friendly_id}.pdf`);
                })
            ),
        {
            dispatch: false,
        }
    );

    constructor(
        private actions$: Actions,
        private service: QuotationsServiceBase,
        private router: Router,
        private store: Store<fromQuotations.QuotationsPartialState>
    ) {}
}
